import { useMemo, useRef, useState } from "react";
import {
  GoogleMap,
  Marker,
  useLoadScript,
  Autocomplete,
} from "@react-google-maps/api";
import { motion } from "framer-motion";

const containerStyle = {
  width: "100%",
  height: "420px",
  borderRadius: "20px",
};

const defaultCenter = {
  lat: -1.286389,
  lng: 36.817223,
};

export default function MapPickerModal({ onClose, onSelect }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    libraries: ["places"],
  });

  const [markerPos, setMarkerPos] = useState(defaultCenter);
  const [searchValue, setSearchValue] = useState("");
  const autocompleteRef = useRef(null);

  const coordinatesLabel = useMemo(
    () => `${markerPos.lat.toFixed(6)}, ${markerPos.lng.toFixed(6)}`,
    [markerPos]
  );

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;

    const newPos = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    };

    setMarkerPos(newPos);
    setSearchValue(place.formatted_address || place.name || "");
  };

  if (loadError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-2xl dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-2xl dark:bg-red-500/10">
            ⚠️
          </div>
          <h2 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
            Map failed to load
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Please check your Google Maps configuration and try again.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-6 inline-flex items-center justify-center rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700 dark:border-slate-700 dark:border-t-slate-200" />
            <h2 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
              Loading map
            </h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Preparing map services and location search...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="flex min-h-full items-start justify-center px-4 py-6 sm:py-10">
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="flex w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 max-h-[calc(100vh-3rem)]"
        >
          {/* Header */}
          <div className="shrink-0 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                  Location Picker
                </div>

                <h2 className="mt-3 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                  Pick Location
                </h2>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Search, click, or drag the pin to select the exact location.
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                ×
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-5">
              {/* Search + coords */}
              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-950/30">
                <div className="mb-4">
                  <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                    Search & Selection
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Find a place quickly or set the marker directly on the map.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_auto]">
                  <Autocomplete
                    onLoad={(ref) => {
                      autocompleteRef.current = ref;
                    }}
                    onPlaceChanged={handlePlaceChanged}
                  >
                    <div className="relative">
                      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
                        🔎
                      </span>
                      <input
                        type="text"
                        placeholder="Search place, landmark, or address..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
                      />
                    </div>
                  </Autocomplete>

                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-900">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                      Coordinates
                    </p>
                    <p className="mt-1 font-medium text-slate-900 dark:text-white">
                      {coordinatesLabel}
                    </p>
                  </div>
                </div>
              </section>

              {/* Map */}
              <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      Map Selection
                    </h3>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Click anywhere on the map or drag the marker to refine the
                      selected location.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setMarkerPos(defaultCenter)}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    Reset to Nairobi
                  </button>
                </div>

                <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-sm dark:border-slate-800">
                  <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={markerPos}
                    zoom={14}
                    onClick={(e) =>
                      setMarkerPos({
                        lat: e.latLng.lat(),
                        lng: e.latLng.lng(),
                      })
                    }
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: false,
                    }}
                  >
                    <Marker
                      position={markerPos}
                      draggable
                      onDragEnd={(e) =>
                        setMarkerPos({
                          lat: e.latLng.lat(),
                          lng: e.latLng.lng(),
                        })
                      }
                    />
                  </GoogleMap>
                </div>
              </section>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 flex flex-col-reverse gap-3 border-t border-slate-200 bg-white px-6 py-4 sm:flex-row sm:justify-between dark:border-slate-800 dark:bg-slate-900">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Selected position:{" "}
              <span className="font-medium text-slate-900 dark:text-white">
                {coordinatesLabel}
              </span>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  onSelect(markerPos);
                  onClose?.();
                }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
              >
                Confirm Location
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}